import packageJson from '../../package.json';
import {
  getChartsAttributionContext,
  initChartsAttribution,
  UTM_KEYS,
} from './chartsAttribution';

const CHARTS_SESSION_STARTED = 'charts_session_started';
const CHARTS_HANDOFF_RECEIVED = 'charts_handoff_received';
const LANDING_STORAGE_KEY = 'ph_charts_landing_context';
const SESSION_FIRED_KEY = 'ph_charts_session_started_id';

function resolveEntrySource({ handoffId, utmSource, referrer, origin } = {}) {
  if (handoffId) {
    return 'inc';
  }
  if (utmSource) {
    return 'utm';
  }
  if (referrer) {
    try {
      const referrerOrigin = new URL(referrer).origin;
      if (origin && referrerOrigin === origin) {
        return 'direct';
      }
      return 'referral';
    } catch {
      return 'referral';
    }
  }
  return 'direct';
}

function buildLandingContext() {
  const attribution = getChartsAttributionContext();
  const params = new URLSearchParams(window.location.search);
  const utms = {};

  UTM_KEYS.forEach((key) => {
    const value = params.get(key) || attribution[key];
    if (value) {
      utms[key] = value;
    }
  });

  return {
    landing_path: attribution.landing_path || window.location.pathname || '/',
    entry_source: resolveEntrySource({
      handoffId: attribution.handoff_id,
      utmSource: utms.utm_source,
      referrer: document.referrer,
      origin: window.location.origin,
    }),
    ...(attribution.handoff_id ? { handoff_id: attribution.handoff_id } : {}),
    ...utms,
  };
}

export function captureLandingContext() {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const existing = window.sessionStorage.getItem(LANDING_STORAGE_KEY);
    if (existing) {
      return JSON.parse(existing);
    }
    const context = buildLandingContext();
    window.sessionStorage.setItem(LANDING_STORAGE_KEY, JSON.stringify(context));
    return context;
  } catch {
    return buildLandingContext();
  }
}

function sessionProperties(extraProperties = {}) {
  const landing = captureLandingContext();
  const properties = {
    landing_path: landing.landing_path,
    entry_source: landing.entry_source,
    ...extraProperties,
  };

  if (landing.handoff_id) {
    properties.handoff_id = landing.handoff_id;
  }

  UTM_KEYS.forEach((key) => {
    if (landing[key]) {
      properties[key] = landing[key];
    }
  });

  return properties;
}

function captureHandoffReceived(posthogClient, handoffId) {
  if (!posthogClient?.capture || !handoffId) {
    return;
  }
  const landing = buildLandingContext();
  const sessionId = posthogClient.get_session_id?.();
  const properties = {
    handoff_id: handoffId,
    landing_path: landing.landing_path || window.location.pathname || '/',
    entry_source: 'inc',
    app_version: packageJson.version,
    ...(sessionId ? { session_id: sessionId } : {}),
  };
  UTM_KEYS.forEach((key) => {
    if (landing[key]) {
      properties[key] = landing[key];
    }
  });
  posthogClient.capture(CHARTS_HANDOFF_RECEIVED, properties);
}

export function bootstrapChartsAttribution(posthogClient) {
  const { handoffReceived, handoffId } = initChartsAttribution();
  if (handoffReceived && handoffId) {
    captureHandoffReceived(posthogClient, handoffId);
  }
  maybeCaptureAnonymousChartsSession(posthogClient);
}

function maybeCaptureAnonymousChartsSession(posthogClient) {
  if (!posthogClient?.capture) {
    return;
  }
  const attribution = getChartsAttributionContext();
  const hasUtms = UTM_KEYS.some((key) => Boolean(attribution[key]));
  if (!attribution.handoff_id && !hasUtms) {
    return;
  }
  captureChartsSessionStarted(posthogClient, {
    plan_tier: 'free',
    subscription_status: 'active',
  });
}

export function captureChartsSessionStarted(posthog, extraProperties = {}) {
  if (!posthog?.capture) {
    return false;
  }

  const sessionId = posthog.get_session_id?.();
  if (!sessionId) {
    return false;
  }

  try {
    if (window.sessionStorage.getItem(SESSION_FIRED_KEY) === sessionId) {
      return false;
    }
    window.sessionStorage.setItem(SESSION_FIRED_KEY, sessionId);
  } catch {
    // If storage is unavailable, still emit once for this call site.
  }

  posthog.capture(CHARTS_SESSION_STARTED, {
    ...sessionProperties(extraProperties),
    session_id: sessionId,
  });
  return true;
}

export function clearChartsSessionFired() {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.removeItem(SESSION_FIRED_KEY);
  } catch {
    // Ignore storage failures on logout.
  }
}
